import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';

const METHODS = [
  {
    code: 'HISTORICAL_AVERAGE',
    label: 'Historical Average',
    intuition: 'Use what has normally happened.',
    guidance: 'Best where the historical series is relatively stable and older observations remain representative.',
  },
  {
    code: 'MOVING_AVERAGE',
    label: 'Moving Average',
    intuition: 'Recent months better represent what happens next.',
    guidance: 'Best where recent behaviour matters more than older history or the series has shifted to a new level.',
  },
  {
    code: 'LINEAR_TREND',
    label: 'Trend',
    intuition: 'Continue the direction we are already seeing.',
    guidance: 'Best where values show a consistent upward or downward direction across the certified history.',
  },
  {
    code: 'SEASONAL_TREND',
    label: 'Seasonal Trend',
    intuition: 'December behaves like December; June behaves like June.',
    guidance: 'Best where monthly behaviour repeats and the recurring seasonal pattern should be preserved.',
  },
  {
    code: 'LAST_VALUE',
    label: 'Last Value',
    intuition: 'Keep the current level until something changes.',
    guidance: 'Useful for reference-like or external series where the latest known level is a sensible neutral baseline.',
  },
  {
    code: 'MANAGEMENT_ESTIMATE',
    label: 'Management Estimate',
    intuition: "Use management's informed expectation.",
    guidance: 'Use where history is weak, a known future event changes the outlook, or business judgement should override the statistical recommendation.',
  },
];

class ForecastMethodGuide extends React.PureComponent {
  render() {
    return (
      <Stack spacing={1.5}>
        <Alert severity="info" icon={<TipsAndUpdatesOutlinedIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Choosing a Forecast Method
          </Typography>
          <Typography variant="body2">
            An initial method is recommended from the certified historical pattern. Review the recommendation, accept it when it makes business sense, or select another method and provide a reason for the override.
          </Typography>
        </Alert>

        <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Forecast method quick guide</Typography>
              <Chip size="small" label="Planner Guide" variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              {METHODS.map((method) => (
                <Grid size = {{ xs:12, md:6, lg:4 }} key={method.code}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{method.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{method.intuition}</Typography>
                      <Typography variant="caption" color="text.secondary">{method.guidance}</Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Growth Rate and Fixed Assumption remain available as advanced/manual methods. They require a Growth % or Forecast Value respectively.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  }
}

export default ForecastMethodGuide;
